import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { fetchPropertyBySlug } from "../services/propertyService";
import { 
  MapPin, 
  Bath, 
  BedDouble, 
  Square, 
  ArrowLeft, 
  Mail, 
  Phone, 
  Loader2, 
  ChevronRight, 
  Share2, 
  Heart,
  CheckCircle2
} from "lucide-react";
import tammyImg from "../assets/teamabout.jpg";

export default function PropertyDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const loadProperty = async () => {
      setLoading(true);
      try {
        const result = await fetchPropertyBySlug(slug);
        if (result) {
          setProperty(result);
        }
      } catch (error) {
        console.error("Error loading property:", error);
      } finally {
        setLoading(false);
      }
    };
    loadProperty();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="bg-[#FAF6F0] min-h-screen flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-12 h-12 text-tropical-500 animate-spin" />
        <p className="text-sm font-medium text-ink-600 font-display">Loading property details...</p>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="bg-[#FAF6F0] min-h-screen flex flex-col">
        <Navbar isTransparent={false} />
        <div className="flex-grow flex flex-col items-center justify-center px-6 text-center py-24">
          <h1 className="text-3xl sm:text-5xl font-bold text-ink-900 font-display mb-4">
            Property Not Found
          </h1>
          <p className="text-ink-600 max-w-md mb-8 font-light text-sm">
            The listing you are looking for may have been removed, sold, or is temporarily unavailable.
          </p>
          <button
            onClick={() => navigate("/properties")}
            className="px-8 py-3.5 bg-brand-dark text-white text-xs font-bold uppercase tracking-wider rounded-xl hover:bg-brand-dark/95 transition-all shadow-md cursor-pointer font-sans"
          >
            Return to Collection
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const data = property.data;
  const isMLS = property.source === "mls";
  const images = data.images || [];

  // Normalize details
  const title = isMLS ? data.Name : data.title;
  const rawPrice = isMLS ? (data.ListPrice || data.Price) : data.price;
  const price = isMLS 
    ? (data.ui_price || (rawPrice ? `$${Number(rawPrice).toLocaleString()}` : "$0")) 
    : `$${Number(data.price || 0).toLocaleString()}`;
  const locationText = isMLS 
    ? `${data.address_line1 || ""}${data.City ? `, ${data.City}` : ""}` 
    : (data.address || "St. John, USVI");
  const beds = isMLS ? data.BedsTotal : data.beds;
  const baths = isMLS ? data.BathsTotal : data.baths;
  const sqft = isMLS
    ? (data.lot_size_acres ? `${data.lot_size_acres} Acres` : (data.lot_size_sqft ? `${data.lot_size_sqft} SqFt` : "N/A"))
    : `${data.square_feet || "N/A"} SqFt`;

  const mlsNumber = isMLS ? data.mls_number : (data.id ? `#${data.id}` : "N/A");
  const subcategory = isMLS ? data.PropertyType : (data.subcategory || data.property_type || "Residential");

  const handleInquiryRedirect = () => {
    navigate("/contact", {
      state: {
        propertyInfo: {
          title: title,
          price: price,
          location: locationText || "St. John, USVI",
          mlsNumber: mlsNumber,
          interest: "Buy Property"
        }
      }
    });
  };

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <div className="bg-[#FAF6F0] min-h-screen font-sans flex flex-col">
      <Navbar isTransparent={false} />

      {/* Main Container */}
      <main className="flex-grow pt-24 lg:pt-32 pb-24 px-6 md:px-12 max-w-7xl mx-auto w-full">
        
        {/* Back button */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink-600 hover:text-tropical-600 transition-colors group cursor-pointer font-display"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Properties
          </button>
        </div>

        {/* Top Grid: Gallery + Quick Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-16">
          
          {/* Left: Gallery */}
          <div className="lg:col-span-7 flex flex-col gap-4">
            <div className="relative aspect-[16/10] overflow-hidden rounded-3xl border border-[#E8E3DC] shadow-lg bg-gray-100">
              <AnimatePresence mode="wait">
                <motion.img
                  key={activeImage}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  src={images[activeImage] || "https://images.unsplash.com/photo-1512917774080-9991f1c4c750"}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              </AnimatePresence>

              {/* Status & MLS Badges */}
              <div className="absolute top-4 left-4 flex gap-2">
                <span className="px-3.5 py-1.5 bg-brand-dark/90 backdrop-blur-md text-white text-[10px] font-bold uppercase tracking-wider rounded-full shadow-md font-display">
                  {isMLS ? "MLS Listing" : "Exclusive"}
                </span>
                <span className="px-3.5 py-1.5 bg-white/90 backdrop-blur-md text-ink-900 text-[10px] font-bold uppercase tracking-wider rounded-full shadow-md font-display">
                  {data.status || "Active"}
                </span>
              </div>
            </div>

            {/* Thumbnail Carousel */}
            {images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(idx)}
                    className={`relative shrink-0 w-24 h-16 rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                      activeImage === idx
                        ? "border-tropical-500 scale-95 shadow-md"
                        : "border-[#E8E3DC] opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Title, Price, Quick Specs */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="bg-white rounded-3xl p-8 border border-[#E8E3DC] shadow-lg flex flex-col gap-6">
              
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-tropical-500 mb-2 font-display">
                  {subcategory}
                </p>
                <h1 className="text-3xl sm:text-4xl font-bold text-ink-900 font-display leading-tight mb-3">
                  {title}
                </h1>
                <p className="flex items-center gap-1.5 text-ink-600 text-sm font-light">
                  <MapPin className="w-4 h-4 text-tropical-500 shrink-0" />
                  {locationText}
                </p>
              </div>

              {/* Price */}
              <div className="py-4 border-y border-gray-100">
                <span className="text-xs uppercase font-bold text-ink-400 block mb-1">Asking Price</span>
                <span className="text-4xl sm:text-5xl font-extrabold text-ink-900 font-display">
                  {price}
                </span>
              </div>

              {/* Key Specs Grid */}
              <div className="grid grid-cols-3 gap-4 py-2">
                <div className="flex flex-col items-center justify-center p-3 bg-[#FAF8F5] rounded-2xl border border-[#E8E3DC]">
                  <BedDouble className="w-5 h-5 text-tropical-500 mb-1" />
                  <span className="text-[10px] uppercase font-bold text-ink-500">Beds</span>
                  <span className="text-base font-bold text-ink-900">{beds || "0"}</span>
                </div>
                <div className="flex flex-col items-center justify-center p-3 bg-[#FAF8F5] rounded-2xl border border-[#E8E3DC]">
                  <Bath className="w-5 h-5 text-tropical-500 mb-1" />
                  <span className="text-[10px] uppercase font-bold text-ink-500">Baths</span>
                  <span className="text-base font-bold text-ink-900">{baths || "0"}</span>
                </div>
                <div className="flex flex-col items-center justify-center p-3 bg-[#FAF8F5] rounded-2xl border border-[#E8E3DC]">
                  <Square className="w-5 h-5 text-tropical-500 mb-1" />
                  <span className="text-[10px] uppercase font-bold text-ink-500">Size</span>
                  <span className="text-xs font-bold text-ink-900 truncate">{sqft}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 pt-2">
                <button
                  onClick={handleInquiryRedirect}
                  className="w-full py-4 bg-brand-dark hover:bg-brand-dark/95 text-white text-xs font-bold uppercase tracking-widest rounded-xl transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer font-sans"
                >
                  <Mail className="w-4 h-4" /> Request Private Showing
                </button>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={handleShare}
                    className="py-3 px-4 bg-[#FAF8F5] hover:bg-gray-100 border border-[#E8E3DC] rounded-xl text-xs font-bold text-ink-800 flex items-center justify-center gap-2 transition-all cursor-pointer"
                  >
                    {copied ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-green-600" /> Link Copied
                      </>
                    ) : (
                      <>
                        <Share2 className="w-4 h-4" /> Share
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleInquiryRedirect}
                    className="py-3 px-4 bg-[#FAF8F5] hover:bg-gray-100 border border-[#E8E3DC] rounded-xl text-xs font-bold text-ink-800 flex items-center justify-center gap-2 transition-all cursor-pointer"
                  >
                    <Heart className="w-4 h-4 text-tropical-500" /> Enquire
                  </button>
                </div>
              </div>

            </div>
          </div>

        </div>

        {/* Lower Grid: Description, Specs Table & Agent Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          
          {/* Left 8 Cols: Description & Specs Details */}
          <div className="lg:col-span-8 flex flex-col gap-10">
            
            {/* Description Card */}
            <div className="bg-white rounded-3xl p-8 sm:p-10 border border-[#E8E3DC] shadow-md">
              <h3 className="text-xs font-bold uppercase tracking-widest text-tropical-500 mb-4 font-display">
                Property Overview
              </h3>
              
              <div className="relative">
                <div
                  className={`transition-all duration-500 overflow-hidden ${
                    isExpanded ? "max-h-[1000px]" : "max-h-48"
                  }`}
                >
                  <p className="text-ink-700 text-base leading-relaxed font-light whitespace-pre-line">
                    {data.description ||
                      "Indulge in island luxury with this exceptional St. John real estate opportunity. Offering privacy, stunning tropical surroundings, and unparalleled proximity to premier island beaches and local amenities."}
                  </p>
                </div>

                {data.description && data.description.length > 250 && (
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="mt-4 text-xs font-bold uppercase tracking-wider text-tropical-600 hover:text-ink-900 transition-colors flex items-center gap-1 cursor-pointer font-display"
                  >
                    {isExpanded ? "Show Less" : "Read Full Description"}{" "}
                    <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? "-rotate-90" : "rotate-90"}`} />
                  </button>
                )}
              </div>
            </div>

            {/* Specification Details Table */}
            <div className="bg-white rounded-3xl p-8 sm:p-10 border border-[#E8E3DC] shadow-md">
              <h3 className="text-xs font-bold uppercase tracking-widest text-tropical-500 mb-6 font-display">
                Detailed Specifications
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                    <span className="text-xs text-ink-500 font-light">MLS Reference</span>
                    <span className="text-sm font-bold text-ink-900">{mlsNumber}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                    <span className="text-xs text-ink-500 font-light">Property Type</span>
                    <span className="text-sm font-bold text-ink-900 capitalize">{subcategory}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                    <span className="text-xs text-ink-500 font-light">Listing Status</span>
                    <span className="text-sm font-bold text-tropical-600">{data.status || "Active"}</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                    <span className="text-xs text-ink-500 font-light">Island Location</span>
                    <span className="text-sm font-bold text-ink-900">St. John, USVI</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                    <span className="text-xs text-ink-500 font-light">City / Area</span>
                    <span className="text-sm font-bold text-ink-900">{data.City || "St. John"}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                    <span className="text-xs text-ink-500 font-light">Postal Code</span>
                    <span className="text-sm font-bold text-ink-900">{data.PostalCode || "00831"}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Right 4 Cols: Agent Contact Card */}
          <div className="lg:col-span-4">
            <div className="bg-white rounded-3xl p-8 border border-[#E8E3DC] shadow-lg sticky top-28 flex flex-col gap-6">
              
              <div className="flex items-center gap-4 pb-6 border-b border-gray-100">
                <img
                  src={tammyImg}
                  alt="Tammy Donnelly"
                  className="w-16 h-16 rounded-full object-cover border-2 border-tropical-500 shrink-0"
                />
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-tropical-500 font-display">
                    Listing Broker
                  </p>
                  <h4 className="text-xl font-bold text-ink-900 font-display">Tammy Donnelly</h4>
                  <p className="text-xs text-ink-500 font-light">Principal Broker</p>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <a
                  href="tel:+13406436068"
                  className="flex items-center gap-3 text-ink-900 hover:text-tropical-600 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-full bg-[#FAF8F5] border border-[#E8E3DC] flex items-center justify-center group-hover:border-tropical-500">
                    <Phone className="w-4 h-4 text-brand-dark" />
                  </div>
                  <span className="font-bold text-sm">+1 (340) 643-6068</span>
                </a>

                <a
                  href="mailto:340realestateco@gmail.com"
                  className="flex items-center gap-3 text-ink-900 hover:text-tropical-600 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-full bg-[#FAF8F5] border border-[#E8E3DC] flex items-center justify-center group-hover:border-tropical-500">
                    <Mail className="w-4 h-4 text-brand-dark" />
                  </div>
                  <span className="font-medium text-xs truncate">340realestateco@gmail.com</span>
                </a>
              </div>

              <button
                onClick={handleInquiryRedirect}
                className="w-full py-4 bg-brand-dark hover:bg-brand-dark/95 text-white text-xs font-bold uppercase tracking-widest rounded-xl transition-all shadow-md cursor-pointer font-sans"
              >
                Inquire About Property
              </button>
            </div>
          </div>

        </div>

      </main>

      <Footer />
    </div>
  );
}
